import { type } from 'os'
import { paginationDefault, MultiSelectAll } from './constants'

export function getVal(pathString, obj) {
  let path = pathString
  if (!Array.isArray(pathString)) {
    /*
      If pathString is not an array, then this statement replaces any
      instances of `[<digit>]` with `.<digit>` using a regular expression
      (/\[(\d)\]/g) and then splits pathString into an array using the
      delimiter `.`. The g flag in the regex indicates that we want to
      replace all instances of [<digit>] (not just the first instance).
      We're doing this because the walker needs each path segment to be
      a valid dot notation property.

      For example, if we have a path string of 'foo[0].bar', we need to
      convert it to 'foo.0.bar' so that we can split it into an array
      of ['foo', '0', 'bar'].
     */
    path = pathString.replace(/\[(\d)\]/g, '.$1').split('.')
  }

  return path.reduce((acc, part) => {
    const currentVal = acc[part]
    path.shift()
    if (Array.isArray(currentVal)) {
      const [nextPart] = path
      const nextPartIndex = Number(nextPart)
      path.shift()
      if (nextPart) {
        if (isNaN(nextPartIndex)) {
          return getVal(path, currentVal.map(aObj => aObj[nextPart]).flat())
        }

        return getVal(path, obj[part][nextPartIndex])
      }

      return currentVal
    }

    if (!currentVal) {
      path.splice(0)
    }

    return getVal(path, currentVal)
  }, obj)
}

/**
 * @function pluck
 * @description
 * Takes an array of properties or an object with key-value pairs and returns a new object containing only
 * the specified properties from the input data object.
 * @param {(Array<string>|Object<string, (string|string[])>)} properties - An array of properties or an object with key-value pairs.
 *   If an array of properties is provided, it should contain strings representing the properties to be retrieved
 *   from the data object. These properties should not be dot paths/nested. For nesting pass an object instead.
 *   If an object is provided, it should contain key-value pairs where the key is the property name in the new object,
 *   and the value is the path to retrieve the value from the input data object.
 * @param {Object} data - An object containing the data from which to pluck the specified properties.
 * @returns {Object} A new object containing only the specified properties from the input data object.
 * @example
  const obj = { data: { hasAgency: true } }
  pluck(['data.hasAgency'], obj)
  // { 'data.hasAgency': true }
  OR
  pluck({ hasAgency: 'data.hasAgency' }, obj)
  // { hasAgency: true }
*/
export const pluck = (properties = [], data = {}) => {
  if (Array.isArray(properties)) {
    return properties.reduce((acc, crr) => {
      acc[crr] = getVal(crr, data)
      return acc
    }, {})
  }
  return Object.entries(properties).reduce((acc, [key, path]) => {
    acc[key] = getVal(path, data)
    return acc
  }, {})
}

export const isNull = val => {
  return val === null
}

const falsyArray = [null, undefined, '']
const falsySet = new Set(falsyArray)
export const trimObj = (obj, trimSet = falsySet) =>
  Object.entries(obj).reduce((acc, [key, val]) => {
    if (trimSet.has(val) || (Array.isArray(val) && !val.length)) {
      return acc
    }
    acc[key] = val
    return acc
  }, {})

export const trimFilterState = state =>
  trimObj(state, new Set([...falsyArray, 'All']))

export const paginate = (
  page = paginationDefault.page,
  pageSize = paginationDefault.pageSize,
) => ({
  limit: +pageSize,
  offset: +page * +pageSize,
})

export const parseFieldProps = ({ field, state = {}, prevState = {} }) => {
  switch (typeof field) {
    case 'string':
      return { type: field }
    case 'object':
    case 'boolean':
      return field
    case 'function':
      return field(state, prevState)
    default:
      throw new Error(`Invalid field type: ${typeof field}`)
  }
}

export const getFirstKeys = (obj, keys = []) => {
  const [firstKey] = Object.keys(obj)
  const firstVal = obj[firstKey]
  keys.push(firstKey)
  if (typeof firstVal === 'object') {
    getFirstKeys(firstVal, keys)
  }
  return keys
}

export const flattenObj = (obj, parent) => {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    const composedKey = parent ? `${parent}.${key}` : key
    if (typeof val === 'object') {
      return { ...acc, ...flattenObj(val, composedKey) }
    }
    return { ...acc, [composedKey]: val }
  }, {})
}

/**
 * Formats a query object based on a provided mapping function.
 *
 * @param {Object} query - The query object to be formatted. Each key-value pair represents a query parameter.
 * @param {Object} queryMap - An object where each key corresponds to a key in the query object and the value is a function that takes the original value and returns an array with two elements: the new key and the new value. If the function is not provided for a key, the original key-value pair is used.
 *
 * @returns {Object} The formatted query object.
 *
 * @example
 * const query = { name: 'John', age: 20 };
 * const queryMap = {
 *   name: val => ['firstName', val.toUpperCase()],
 *   age: val => ['yearsOld', val]
 * };
 * formatQuery(query, queryMap); // Returns { firstName: 'JOHN', yearsOld: 20 }
 */
export const formatQuery = (query, queryMap) =>
  Object.entries(query).reduce((acc, [key, val]) => {
    const [formattedKey, formattedValue = val] = queryMap[key]?.(val) ?? [
      key,
      val,
    ]
    return { ...acc, [formattedKey]: formattedValue }
  }, {})

// Deeply converts objects string values of yes/no, true/false to booleans
export const transformStringBooleans = inputObj => {
  return Object.entries(inputObj).reduce((acc, [key, val]) => {
    const isDate = val instanceof Date
    let updatedValue = val
    if (
      typeof val === 'object' &&
      !isNull(val) &&
      !Array.isArray(val) &&
      !isDate
    ) {
      updatedValue = transformStringBooleans(val)
    }

    if (typeof val === 'string') {
      const lowerVal = val.toLowerCase()
      if (/^(no|false)$/.test(lowerVal)) {
        updatedValue = false
      } else if (/^(yes|true)$/.test(lowerVal)) {
        updatedValue = true
      }
    }
    acc[key] = updatedValue
    return acc
  }, {})
}

export const isLiteralObject = value => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export const filterObj = (obj, filterFn) => {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    if (filterFn({ val, key })) {
      acc[key] = val
    }
    return acc
  }, {})
}

export const convertMultiSelectAllToValues = (values, options) => {
  if (values.includes(MultiSelectAll)) {
    return options.map(option => {
      return option.value ? option.value : option
    })
  }
  return values
}
